import {
  Box,
  Button,
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  Heading,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { sqlImportDuration, type SqlImport } from '@/services/db/sql-import/entity';
import type { SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { ImportSummary } from '@/features/sql-sync';
import { DatabaseDetails } from '../../components/database-details';
import { summaryInput } from '../summary-input';

/** Why the manage button is off for viewers without the edit permission. */
export const MANAGE_PERMISSION_TOOLTIP =
  'Managing syncs requires Workspace manager permissions or greater. Contact your workspace admin to increase your permissions.';

type ImportConfigurationProps = {
  workspaceId: string;
  sqlImport: SqlImport;
  database: SqlImportDatabase | undefined;
  /** "People" or the object type's name. */
  syncTarget: string;
  segment: { id: string; name: string } | null;
  canEdit: boolean;
  /** The Settings tab, where the sync is managed. */
  settingsHref: string;
};

/** The side panel: where the import reads from, how often, and what it does with the rows. */
export function ImportConfiguration({
  workspaceId,
  sqlImport,
  database,
  syncTarget,
  segment,
  canEdit,
  settingsHref,
}: ImportConfigurationProps) {
  return (
    <Box data-test-sql-import-configuration>
      <Heading level="4" color="bold" mb="100">
        Configuration
      </Heading>
      <Box my="200">
        <DescriptionList>
          <DescriptionListTerm>Source</DescriptionListTerm>
          <DescriptionListDetails data-test-sql-import-database-name>
            {database == null ? (
              <Text color="subtle">Unknown source</Text>
            ) : (
              <DatabaseDetails database={database} location={sqlImport.location} />
            )}
          </DescriptionListDetails>
          <DescriptionListTerm>Frequency</DescriptionListTerm>
          <DescriptionListDetails data-test-sql-import-frequency>
            Runs every {sqlImportDuration(sqlImport)}
          </DescriptionListDetails>
          <DescriptionListTerm>Behavior</DescriptionListTerm>
          <DescriptionListDetails data-test-sql-import-behavior>
            <ImportSummary
              workspaceId={workspaceId}
              sqlImport={summaryInput(sqlImport)}
              syncTarget={syncTarget}
              segment={segment}
              withCapital
              withIdentifyBy
            />
          </DescriptionListDetails>
        </DescriptionList>
      </Box>
      <Tooltip isDisabled={canEdit} isCustom={true}>
        {(context) => (
          <>
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              variant="secondary"
              href={settingsHref}
              isDisabled={!canEdit}
              data-test-sql-import-manage
            >
              Manage sync
            </Button>
            <TooltipContent>{MANAGE_PERMISSION_TOOLTIP}</TooltipContent>
          </>
        )}
      </Tooltip>
    </Box>
  );
}
